import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Footer from '../components/Footer';
import { ArrowRight, Eye, EyeSlash, CheckCircle, XCircle } from '@phosphor-icons/react';

export default function RegisterPage() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [company, setCompany] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const navigate = useNavigate();
  const { register } = useAuth();

  const markTouched = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  // Live password validation rules
  const passwordChecks = useMemo(() => ({
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
  }), [password]);

  const passwordValid = passwordChecks.length && passwordChecks.uppercase && passwordChecks.number;
  const passwordsMatch = password === confirmPassword;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (!passwordValid) {
      setError('Password does not meet the requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await register(firstName, lastName, email, password, company || undefined);
    setLoading(false);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  const Check = ({ ok }) => ok 
    ? <CheckCircle size={16} weight="fill" className="text-[#34C759] shrink-0" />
    : <XCircle size={16} weight="fill" className="text-[#C7C7CC] shrink-0" />;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col lg:flex-row">
      {/* Left Panel - Branding (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#007AFF] text-white flex-col justify-between p-12">
        <div>
          <div className="flex items-center">
            <span className="text-3xl brand-name-white">Zelvrix</span>
          </div>
        </div>
        <div className="space-y-6">
          <h1 className="text-4xl font-bold leading-tight">
            Start closing<br />more deals today.
          </h1>
          <p className="text-white/70 text-lg max-w-md">
            Join thousands of sales professionals using AI to automate their follow-ups and never miss a lead.
          </p>
        </div>
        <div className="text-white/50 text-sm">&copy; 2026 Zelvrix. All rights reserved.</div>
      </div>
      
      {/* Right Panel - Register Form */}
      <div className="flex-1 flex items-center justify-center px-5 py-8 sm:p-8 overflow-y-auto">
        <div className="w-full max-w-md space-y-5 animate-entrance">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-2">
            <span className="text-2xl brand-name">Zelvrix</span>
          </div>

          <div className="text-center lg:text-left">
            <p className="text-base font-bold text-[#1D1D1F] mb-2" data-testid="register-tagline">
              The CRM where you never type anything.
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1D1D1F]">Create your account</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm rounded-xl" data-testid="register-error">
                {error}
              </div>
            )}

            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="firstName" className="text-[#1D1D1F] font-medium text-sm">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={() => markTouched('firstName')}
                  placeholder="John"
                  required
                  autoComplete="given-name"
                  className={`input-base h-11 rounded-xl text-base ${touched.firstName && !firstName.trim() ? 'ring-2 ring-[#FF3B30]/50' : ''}`}
                  data-testid="register-first-name-input"
                />
                {touched.firstName && !firstName.trim() && (
                  <p className="text-xs text-[#FF3B30]">First name is required</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName" className="text-[#1D1D1F] font-medium text-sm">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => markTouched('lastName')}
                  placeholder="Smith"
                  required
                  autoComplete="family-name"
                  className={`input-base h-11 rounded-xl text-base ${touched.lastName && !lastName.trim() ? 'ring-2 ring-[#FF3B30]/50' : ''}`}
                  data-testid="register-last-name-input"
                />
                {touched.lastName && !lastName.trim() && (
                  <p className="text-xs text-[#FF3B30]">Last name is required</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[#1D1D1F] font-medium text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => markTouched('email')}
                placeholder="you@company.com"
                required
                autoComplete="email"
                inputMode="email"
                className={`input-base h-11 rounded-xl text-base ${touched.email && email && !emailValid ? 'ring-2 ring-[#FF3B30]/50' : ''}`}
                data-testid="register-email-input"
              />
              {touched.email && email && !emailValid && (
                <p className="text-xs text-[#FF3B30]">Please enter a valid email address</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[#1D1D1F] font-medium text-sm">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => markTouched('password')}
                  placeholder="Create a password"
                  required
                  autoComplete="new-password"
                  className={`input-base h-11 rounded-xl pr-12 text-base ${touched.password && password && !passwordValid ? 'ring-2 ring-[#FF3B30]/50' : ''} ${password && passwordValid ? 'ring-2 ring-[#34C759]/50' : ''}`}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {/* Live password strength checklist */}
              {password && (
                <div className="flex flex-col gap-1 mt-2" data-testid="password-requirements">
                  <div className="flex items-center gap-2 text-xs">
                    <Check ok={passwordChecks.length} />
                    <span className={passwordChecks.length ? 'text-[#34C759]' : 'text-[#86868B]'}>At least 8 characters</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Check ok={passwordChecks.uppercase} />
                    <span className={passwordChecks.uppercase ? 'text-[#34C759]' : 'text-[#86868B]'}>1 uppercase letter</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Check ok={passwordChecks.number} />
                    <span className={passwordChecks.number ? 'text-[#34C759]' : 'text-[#86868B]'}>1 number</span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-[#1D1D1F] font-medium text-sm">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => markTouched('confirmPassword')}
                placeholder="Re-enter your password"
                required
                autoComplete="new-password"
                className={`input-base h-11 rounded-xl text-base ${confirmPassword && !passwordsMatch ? 'ring-2 ring-[#FF3B30]/50' : ''} ${confirmPassword && passwordsMatch ? 'ring-2 ring-[#34C759]/50' : ''}`}
                data-testid="register-confirm-password-input"
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-[#FF3B30]" data-testid="password-mismatch-error">Passwords do not match</p>
              )}
              {confirmPassword && passwordsMatch && (
                <p className="text-xs text-[#34C759]">Passwords match</p>
              )}
            </div>

            {/* Company */}
            <div className="space-y-1.5">
              <Label htmlFor="company" className="text-[#1D1D1F] font-medium text-sm">
                Company Name <span className="text-[#86868B] font-normal">(optional)</span>
              </Label>
              <Input
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Corporation"
                autoComplete="organization"
                className="input-base h-11 rounded-xl text-base"
                data-testid="register-company-input"
              />
            </div>

            {/* Terms checkbox */}
            <div className="flex items-start gap-3 pt-1">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#007AFF] focus:ring-[#007AFF] cursor-pointer"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-xs text-[#86868B] leading-relaxed cursor-pointer">
                I have read and agree to the{' '}
                <Link to="/terms-and-conditions" target="_blank" className="text-[#007AFF] font-medium hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" target="_blank" className="text-[#007AFF] font-medium hover:underline">
                  Privacy Policy
                </Link>
                , including the transfer of my data to third party servers outside my country of residence.
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading || !agreedToTerms}
              className="w-full btn-primary h-12 rounded-xl text-base"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Sign Up'}
              {!loading && <ArrowRight size={20} className="ml-2" />}
            </Button>
          </form>

          <p className="text-center text-sm text-[#86868B]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#007AFF] font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
